import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ExportMenu from "@/components/appointmentRequest/exportMenu";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import AssignmentEnrolleeNames from "@/components/lms/assignmentEnrolleeNames";
import LmsIntervalValue from "@/components/lms/lmsIntervalValue";
import {
  ASSIGNMENT_RULES_EXCLUDED_TYPES,
  KNOWN_ASSIGNMENT_TYPES,
  getAssignmentTypeLabel,
  getAssignmentTypeOptions,
} from "@/components/lms/enrolmentAssignmentTargetSelection";
import { formatDateValue, LmsMode } from "@/components/lms/lms.utils";

const TABLE_ASSIGNMENT_TYPES = KNOWN_ASSIGNMENT_TYPES.filter(
  (type) => !ASSIGNMENT_RULES_EXCLUDED_TYPES.includes(type)
);
const TABLE_ASSIGNMENT_LIST_PARAMETERS = {
  "type__in!": ASSIGNMENT_RULES_EXCLUDED_TYPES.join(","),
};

interface Props {
  mode?: LmsMode;
  onCreate?: () => void;
  onEdit?: (assignment: any) => void;
  refreshKey?: number;
  onRefreshed?: () => void;
}

const isReadOnlyAssignmentRow = (row: any) =>
  Boolean((row?.original ?? row)?.is_read_only);

export default function LmsEnrolmentAssignmentsTable({
  mode = "administrator",
  onCreate,
  onEdit,
  refreshKey = 0,
  onRefreshed,
}: Props) {
  const { t } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const [exportParams, setExportParams] = useState<Record<string, any>>({});

  const assignmentTypeFilterOptions = useMemo(
    () =>
      getAssignmentTypeOptions(t, {
        usesManagerCompanyScope: mode === "manager",
        types: TABLE_ASSIGNMENT_TYPES,
      }),
    [mode, t]
  );

  const columns = useMemo(
    () => [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
      },
      {
        header: t("course_title"),
        accessorKey: "course.name",
        id: "course",
        customColumnFilterId: "course__title",
        accessorFn: (row: any): ReactNode => row.course?.name ?? "-",
      },
      {
        header: t("assignment_type"),
        accessorKey: "type",
        id: "type",
        filterVariant: "select",
        filterSelectOptions: assignmentTypeFilterOptions,
        setNoFilterParameter: true,
        accessorFn: (row: any): ReactNode =>
          getAssignmentTypeLabel(row.type, t, {
            usesManagerCompanyScope: mode === "manager",
          }),
      },
      {
        header: t("enrollees"),
        accessorKey: "enrollees",
        id: "enrollees",
        customColumnFilterId: "enrollee_name",
        enableSorting: false,
        enableColumnFilter: true,
        size: 400,
        accessorFn: (row: any): ReactNode => (
          <AssignmentEnrolleeNames assignment={row} />
        ),
      },
      ...(mode === "manager"
        ? []
        : [
            {
              header: t("company"),
              accessorKey: "associated_company_subject_set",
              id: "company_subject",
              customColumnFilterId: "company_subject_name",
              customSortingKey: "company_subject",
              accessorFn: (row: any): ReactNode => (
                <AssignmentEnrolleeNames
                  assignment={{
                    type: "multiple_companies",
                    company_subject_set:
                      row.associated_company_subject_set ?? [],
                  }}
                />
              ),
            },
          ]),
      {
        header: t("completion_interval_in_days"),
        accessorKey: "completion_interval_in_days",
        id: "completion_interval_in_days",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 0 },
        },
        accessorFn: (row: any): ReactNode => (
          <LmsIntervalValue
            value={row.completion_interval_in_days}
            inheritedFrom={row.inherited_from?.completion_interval_in_days}
          />
        ),
      },
      {
        header: t("validity_interval_in_days"),
        accessorKey: "validity_interval_in_days",
        id: "validity_interval_in_days",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 0 },
        },
        accessorFn: (row: any): ReactNode => (
          <LmsIntervalValue
            value={row.validity_interval_in_days}
            inheritedFrom={row.inherited_from?.validity_interval_in_days}
          />
        ),
      },
      {
        header: t("assigned_on"),
        accessorKey: "created_on",
        id: "created_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any): ReactNode =>
          formatDateValue(row.created_on, true),
      },
      {
        header: t("assigned_by"),
        accessorKey: "created_by.name",
        id: "created_by",
        customColumnFilterId: "created_by_name",
        accessorFn: (row: any): ReactNode => row.created_by?.name ?? "-",
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any): ReactNode =>
          formatDateValue(row.modified_on, true),
      },
      {
        header: t("modified_by"),
        accessorKey: "modified_by.name",
        id: "modified_by",
        customColumnFilterId: "modified_by_name",
        accessorFn: (row: any): ReactNode => row.modified_by?.name ?? "-",
      },
    ],
    [assignmentTypeFilterOptions, mode, t]
  );

  const topToolbarAction = useCallback(
    () => (
      <ExportMenu
        entity="enrolmentAssignment"
        exportParameter="export"
        parametersToRender={exportParams}
        handleError={handleError}
        shouldNotImpersonate={mode !== "manager"}
        buttonLabel="export_all"
        menuItems={[
          { label: "export_as", format: "csv", icon: "text" },
          { label: "export_as", format: "xlsx", icon: "chart" },
        ]}
      />
    ),
    [exportParams, handleError]
  );

  const handleEdit = useCallback(
    (row: any) => {
      const assignment = row?.original ?? row;
      if (assignment?.is_read_only) return;

      onEdit?.(assignment);
    },
    [onEdit]
  );

  return (
    <>
      <div>
        <GenericCrudTable
          key={`enrolment-assignments-table-${refreshKey}`}
          dataTestId="lms-enrolment-assignments-table"
          entity="enrolmentAssignment"
          columns={columns}
          additionalUrlParameter={{
            ...TABLE_ASSIGNMENT_LIST_PARAMETERS,
            ...(mode === "manager" || mode === "administrator"
              ? { prefer_translation: "true" }
              : {}),
          }}
          customCreateHandler={onCreate}
          customEditHandler={handleEdit}
          customTopToolBarAction={topToolbarAction}
          onExportParamsChange={({
            globalFilter,
            additionalUrlParameters,
            ordering,
          }) => {
            const nextExportParams: Record<string, any> = {
              additionalUrlParameters,
            };
            if (globalFilter?.trim())
              nextExportParams.search = globalFilter.trim();
            if (ordering) nextExportParams.ordering = ordering;
            setExportParams(nextExportParams);
          }}
          disableAddItem={!onCreate}
          refreshTableAfterAction={onRefreshed}
          disableEditButtonForRow={isReadOnlyAssignmentRow}
          disableDeleteButtonForRow={isReadOnlyAssignmentRow}
          enableRowSelection={(row: any) => !isReadOnlyAssignmentRow(row)}
          enableBulkDelete={(selectedRows: any[]) =>
            selectedRows.every((row) => !isReadOnlyAssignmentRow(row))
          }
          allowColumnFiltering
          showColumnFilterbyDefault
          columnMinSize={40}
          disableGlobalFilter
          customColumnVisibility={{
            created_on: true,
            created_by: true,
          }}
        />
      </div>

      {errorModal}
    </>
  );
}
